import { observable, action, makeObservable } from 'mobx'
import SilentListStore from 'stores/SilentListStore'
import WithholdingService from 'services/WithholdingService'
import Paginator from 'models/Paginator'
import Sorter from 'models/Sorter'

class WithholdingStore extends SilentListStore {
    DEFAULT_SORT_FIELD = 'createdAt'
    DEFAULT_SORT_SENSE = 'DESC'
    currentPage = 1
    withholdings = []
    nameTextSearch = null
    tabSelected = 'all'
    filterSelectedCompany = null
    filterSelectedOrigin = null
    filterSelectedBroker = null
    optionTypeSelected = null

    paginator = Paginator.empty()
    sorter = Sorter.fromJson({
        sortField: this.DEFAULT_SORT_FIELD,
        sortSense: this.DEFAULT_SORT_SENSE,
    })

    constructor() {
        super({ itemsKey: 'withholdings' })

        makeObservable(this, {
            currentPage: observable,
            withholdings: observable,
            nameTextSearch: observable,
            tabSelected: observable,
            filterSelectedCompany: observable,
            filterSelectedOrigin: observable,
            filterSelectedBroker: observable,
            optionTypeSelected: observable,
            paginator: observable,
            sorter: observable,
            changePage: action,
            changeSort: action,
            setSort: action,
            filterByName: action,
            filterByCompany: action,
            filterByOrigin: action,
            filterByBroker: action,
            filterByType: action,
            setTabSelected: action,
            listWithholdings: action,
            ...this.getSilentListAnnotations(),
        })

        this.withholdingService = new WithholdingService()
    }

    getListCacheKey() {
        return [
            this.tabSelected || '',
            this.nameTextSearch || '',
            this.filterSelectedCompany || '',
            this.filterSelectedOrigin || '',
            this.filterSelectedBroker || '',
            this.optionTypeSelected || '',
            this.currentPage,
            this.sorter.sortField,
            this.sorter.sortSense,
        ].join('|')
    }

    setDefaultSort() {
        this.setSort(this.DEFAULT_SORT_FIELD, this.DEFAULT_SORT_SENSE)
    }

    changePage(page) {
        this.currentPage = page
    }

    changeSort(sortField) {
        this.sorter.changeSort(sortField)
    }

    setSort(sortField, sortSense) {
        if (sortField && sortSense) {
            this.sorter.setSort(sortField, sortSense)
        } else {
            this.setDefaultSort()
        }
    }

    filterByName(text) {
        this.nameTextSearch = text
    }

    filterByCompany(company) {
        this.filterSelectedCompany = company
    }

    filterByOrigin(group) {
        this.filterSelectedOrigin = group
    }

    filterByBroker(broker) {
        this.filterSelectedBroker = broker
    }

    filterByType(type) {
        this.optionTypeSelected = type
    }

    setTabSelected(tab) {
        this.tabSelected = tab
    }

    async listWithholdings({ silent = false } = {}) {
        await this.fetchList({
            silent,
            onRetry: () => this.listWithholdings(),
            request: async () => {
                const params = {
                    page: this.currentPage,
                    search: this.nameTextSearch,
                    [`sort[${this.sorter.sortField}]`]: this.sorter.sortSense,
                    insuranceCompany: this.filterSelectedCompany || null,
                    group: this.filterSelectedOrigin || null,
                    broker: this.filterSelectedBroker || null,
                    type: this.optionTypeSelected || null,
                }
                const { withholdings, paginator } = await this.withholdingService.listWithholdings(
                    params
                )

                return {
                    items: withholdings,
                    paginator,
                }
            },
        })
    }
}

export default WithholdingStore
